import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HelpCircle, MessageCircle, Mail } from 'lucide-react';
const faqs = [{
  q: '¿Sirve si nunca trabajé como freelancer?',
  a: 'Sí. El Tomo 1 arranca desde cero y el Tomo 2 te ayuda a elegir exactamente qué servicio podés ofrecer hoy, aunque no tengas experiencia previa. El Bono 3 te da el perfil listo para publicar en 1 hora.'
}, {
  q: '¿Cómo recibo el material?',
  a: 'Inmediatamente después de tu pago recibís un email con el link de descarga de todos los PDFs. Acceso instantáneo desde cualquier dispositivo en menos de 5 minutos.'
}, {
  q: '¿Funciona si tengo trabajo y poco tiempo?',
  a: 'Sí. El sistema está diseñado para personas que empiezan con 45-60 minutos por día. El Tomo 4 incluye un protocolo de búsqueda de clientes optimizado para ese tiempo disponible.'
}, {
  q: '¿Qué lo diferencia de un curso de YouTube?',
  a: 'Los cursos de YouTube dan información general. Esto es un sistema completo con templates, scripts y checklists listos para usar desde hoy. No tenés que armar nada, solo copiar y adaptar.'
}, {
  q: '¿Tiene garantía real?',
  a: 'Sí. 7 días de garantía total. Si no estás conforme por cualquier razón, te devolvemos el 100% sin preguntas ni formularios complicados.'
}, {
  q: '¿Necesito herramientas pagas?',
  a: 'No. Todo el sistema funciona con herramientas gratuitas: Workana, Fiverr, Google Sheets, Canva (gratis) y Payoneer (gratis). No necesitás invertir en software para empezar.'
}];
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <section className="py-16 bg-petroleum/30 border-y border-white/5 relative overflow-hidden" data-id="element-212">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-electric/5 blur-[120px] rounded-full pointer-events-none" data-id="element-213"></div>

      <div className="container mx-auto px-4 relative z-10" data-id="element-214">
        {/* Framed Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="relative max-w-3xl mx-auto mb-12" data-id="element-215">
          <div className="absolute -inset-1 bg-gradient-to-r from-electric via-gold to-electric rounded-3xl blur-xl opacity-30 animate-pulse" data-id="element-216"></div>
          <div className="relative bg-gradient-to-br from-graphite via-petroleum to-graphite border-2 border-electric rounded-3xl p-8 md:p-10 text-center overflow-hidden shadow-2xl shadow-electric/20" data-id="element-217">
            <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{
            backgroundImage: 'linear-gradient(rgba(245,185,66,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(245,185,66,0.6) 1px, transparent 1px)',
            backgroundSize: '24px 24px'
          }} data-id="element-218"></div>
            <div className="absolute top-0 left-0 w-14 h-14 border-t-4 border-l-4 border-electric rounded-tl-3xl pointer-events-none" data-id="element-219"></div>
            <div className="absolute top-0 right-0 w-14 h-14 border-t-4 border-r-4 border-electric rounded-tr-3xl pointer-events-none" data-id="element-220"></div>
            <div className="absolute bottom-0 left-0 w-14 h-14 border-b-4 border-l-4 border-electric rounded-bl-3xl pointer-events-none" data-id="element-221"></div>
            <div className="absolute bottom-0 right-0 w-14 h-14 border-b-4 border-r-4 border-electric rounded-br-3xl pointer-events-none" data-id="element-222"></div>

            <div className="relative z-10" data-id="element-223">
              <div className="inline-flex items-center gap-2 bg-electric text-graphite px-4 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-wider mb-5 shadow-lg shadow-electric/40" data-id="element-224">
                <HelpCircle className="w-3.5 h-3.5" strokeWidth={2.5} data-id="element-225" />
                Preguntas Frecuentes
              </div>
              <h2 className="text-2xl md:text-4xl font-extrabold leading-tight" data-id="element-226">
                <span className="text-white" data-id="element-227">Lo que más </span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-electric via-gold to-electric drop-shadow-[0_0_12px_rgba(245,185,66,0.4)]" data-id="element-228">
                  nos preguntan
                </span>
              </h2>
              <div className="mt-4 mx-auto h-px w-32 bg-gradient-to-r from-transparent via-electric to-transparent opacity-70" data-id="element-229"></div>
              <p className="mt-4 text-sm text-textsecondary" data-id="element-230">
                Todo lo que necesitás saber antes de empezar
              </p>
            </div>
          </div>
        </motion.div>

        {/* FAQ List */}
        <div className="max-w-3xl mx-auto space-y-3" data-id="element-231">
          {faqs.map((faq, i) => {
          const isOpen = openIndex === i;
          return <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.04
          }} className={`relative bg-gradient-to-br from-graphite to-petroleum/60 border rounded-2xl overflow-hidden transition-all duration-300 ${isOpen ? 'border-electric/50 shadow-[0_0_25px_rgba(245,185,66,0.15)]' : 'border-white/10 hover:border-electric/25'}`} data-id="element-232">
                {/* Left accent bar when open */}
                {isOpen && <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-electric via-gold to-electric" data-id="element-233"></div>}

                <button onClick={() => setOpenIndex(isOpen ? null : i)} className="w-full flex items-center justify-between gap-4 p-5 text-left group" aria-expanded={isOpen} data-id="element-234">
                  <div className="flex items-start gap-4 flex-1 min-w-0" data-id="element-235">
                    <span className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs transition-all ${isOpen ? 'bg-electric text-graphite shadow-md shadow-electric/30' : 'bg-petroleum text-electric border border-electric/30 group-hover:bg-electric/10'}`} data-id="element-236">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className={`font-semibold leading-snug pt-1 transition-colors ${isOpen ? 'text-white' : 'text-white/90'}`} data-id="element-237">
                      {faq.q}
                    </span>
                  </div>
                  <motion.div animate={{
                rotate: isOpen ? 180 : 0
              }} transition={{
                duration: 0.25
              }} className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-electric/15 text-electric' : 'bg-white/5 text-textsecondary group-hover:bg-white/10'}`} data-id="element-238">
                    <ChevronDown className="w-4 h-4" strokeWidth={2.5} data-id="element-239" />
                  </motion.div>
                </button>

                <AnimatePresence initial={false} data-id="element-240">
                  {isOpen && <motion.div key="content" initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.25,
                ease: 'easeOut'
              }} className="overflow-hidden" data-id="element-241">
                      <div className="px-5 pb-5 pl-[68px]" data-id="element-242">
                        <div className="pt-4 border-t border-white/5 text-textsecondary leading-relaxed text-sm" data-id="element-243">
                          {faq.a}
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>

        {/* Help footer card */}
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-3xl mx-auto mt-8" data-id="element-244">
          <div className="bg-gradient-to-r from-petroleum via-graphite to-petroleum border border-electric/20 rounded-2xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-245">
            <div className="flex items-center gap-3" data-id="element-246">
              <div className="w-11 h-11 rounded-xl bg-electric/15 border border-electric/30 flex items-center justify-center flex-shrink-0" data-id="element-247">
                <MessageCircle className="w-5 h-5 text-electric" data-id="element-248" />
              </div>
              <div data-id="element-249">
                <p className="text-white font-bold text-sm" data-id="element-250">
                  ¿Te quedó otra duda?
                </p>
                <p className="text-textsecondary text-xs" data-id="element-251">
                  Te respondemos en menos de 24 horas
                </p>
              </div>
            </div>
            <a href="mailto:soportebibliotecasdigitales@gmail.com" className="inline-flex items-center gap-2 bg-electric hover:bg-electric/90 text-graphite font-bold px-5 py-2.5 rounded-xl text-sm transition-all hover:shadow-lg hover:shadow-electric/30" data-id="element-252">
              <Mail className="w-4 h-4" data-id="element-253" />
              Escribinos
            </a>
          </div>
        </motion.div>
      </div>
    </section>;
}